import {
  Badge,
  Box,
  Card,
  HStack,
  Heading,
  Stack,
  Text,
} from "@chakra-ui/react"
import { useQuery, useQueryClient } from "@tanstack/react-query"
import { createFileRoute, useParams } from "@tanstack/react-router"
import { format } from "date-fns"
import { HtmlContent } from "../../components/Common/HtmlContent"

import type { UserPublic } from "@/client"
import { QuestionsService } from "@/client"
import { PageShell, PageSkeleton } from "@/components/Layout"
import { useFeedbackEnabled } from "@/hooks/useFeedbackEnabled"
import { useRouteBreadcrumbs } from "@/navigation/route-meta"
import {
  FeedbackServiceTemp,
  type QuestionFeedbackPublic,
} from "@/services/feedbackService"

export const Route = createFileRoute("/_layout/questions/$questionId/feedback")(
  {
    component: QuestionFeedbackPage,
  },
)

function QuestionFeedbackPage() {
  const { questionId } = useParams({
    from: "/_layout/questions/$questionId/feedback",
  })
  const queryClient = useQueryClient()
  const currentUser = queryClient.getQueryData<UserPublic>(["currentUser"])
  const { enabled: feedbackEnabled } = useFeedbackEnabled()
  const breadcrumbs = useRouteBreadcrumbs("questions.index", [
    { label: "Edit Question", href: `/questions/${questionId}/edit` },
    { label: "Feedback", isCurrent: true },
  ])

  // Get question details
  const { data: question, isLoading: isLoadingQuestion } = useQuery({
    queryKey: ["question", questionId],
    queryFn: () => QuestionsService.getQuestion({ questionId }),
    enabled: feedbackEnabled,
  })

  // Get all feedback for this question (org-wide, requires teacher/admin role)
  const { data: feedbackData, isLoading: isLoadingFeedback } = useQuery<
    QuestionFeedbackPublic[]
  >({
    queryKey: ["organization-feedback", { questionId }],
    queryFn: () =>
      FeedbackServiceTemp.getOrganizationFeedback({
        questionId,
      }),
    enabled: !!currentUser && feedbackEnabled,
  })

  if (!feedbackEnabled) {
    return (
      <PageShell
        title="Question Feedback"
        breadcrumbs={breadcrumbs}
        description="Feedback is disabled for your organization."
      >
        <Card.Root>
          <Card.Body>
            <Text color="fg.muted">
              This organization has disabled feedback.
            </Text>
          </Card.Body>
        </Card.Root>
      </PageShell>
    )
  }

  if (isLoadingQuestion || isLoadingFeedback) {
    return (
      <PageShell
        title="Question Feedback"
        breadcrumbs={breadcrumbs}
        description="Review feedback submitted for this question."
      >
        <Box data-testid="question-feedback-loading">
          <PageSkeleton variant="detail" rows={4} />
        </Box>
      </PageShell>
    )
  }

  if (!question) {
    return (
      <PageShell
        title="Question Feedback"
        breadcrumbs={breadcrumbs}
        description="Review feedback submitted for this question."
      >
        <Text>Question not found</Text>
      </PageShell>
    )
  }

  // Group feedback by quiz
  const feedbackByQuiz: Record<string, QuestionFeedbackPublic[]> = {}
  if (feedbackData) {
    for (const fb of feedbackData) {
      const quizId = fb.quiz_id
      if (!feedbackByQuiz[quizId]) {
        feedbackByQuiz[quizId] = []
      }
      feedbackByQuiz[quizId].push(fb)
    }
  }

  const quizIds = Object.keys(feedbackByQuiz)

  return (
    <PageShell
      title="Question Feedback"
      breadcrumbs={breadcrumbs}
      description="Review feedback grouped by quiz for this question."
    >
      <Stack gap={6}>
        <Card.Root>
          <Card.Body>
            <HtmlContent content={question.question_text} />
          </Card.Body>
        </Card.Root>

        {/* Feedback grouped by quiz */}
        {quizIds.length === 0 ? (
          <Card.Root>
            <Card.Body>
              <Text color="fg.muted">
                No feedback has been submitted for this question yet.
              </Text>
            </Card.Body>
          </Card.Root>
        ) : (
          <Stack gap={6}>
            {quizIds.map((quizId) => {
              const feedbackList = feedbackByQuiz[quizId]

              return (
                <Box key={quizId}>
                  <Heading size="md" mb={4}>
                    Quiz: {quizId}
                  </Heading>
                  <Stack gap={4}>
                    {feedbackList.map((feedback) => (
                      <Card.Root key={feedback.id}>
                        <Card.Body>
                          <Stack gap={4}>
                            {/* Header */}
                            <HStack justify="space-between">
                              <HStack gap={2}>
                                <Badge
                                  colorPalette={
                                    feedback.status === "resolved"
                                      ? "green"
                                      : feedback.status === "read"
                                        ? "blue"
                                        : "orange"
                                  }
                                >
                                  {feedback.status}
                                </Badge>
                                <Text fontSize="sm" color="fg.muted">
                                  Submitted:{" "}
                                  {format(
                                    new Date(feedback.created_at),
                                    "MMM d, yyyy 'at' h:mm a",
                                  )}
                                </Text>
                                {feedback.created_by_name && (
                                  <Text fontSize="sm" color="fg.muted">
                                    • By: {feedback.created_by_name}
                                  </Text>
                                )}
                              </HStack>
                              {feedback.assigned_to_name && (
                                <Badge colorPalette="purple">
                                  Assigned to: {feedback.assigned_to_name}
                                </Badge>
                              )}
                            </HStack>

                            {/* Feedback content */}
                            <Box>
                              <Text fontWeight="semibold" mb={2}>
                                Student Feedback:
                              </Text>
                              <HtmlContent content={feedback.feedback_text} />
                            </Box>

                            {/* Replies */}
                            {feedback.replies.length > 0 && (
                              <Box
                                pl={6}
                                borderLeftWidth="2px"
                                borderLeftColor="border"
                              >
                                <Text fontWeight="semibold" mb={2}>
                                  Teacher Replies:
                                </Text>
                                <Stack gap={3}>
                                  {feedback.replies.map((reply) => (
                                    <Box key={reply.id}>
                                      <Text
                                        fontSize="sm"
                                        color="fg.muted"
                                        mb={1}
                                      >
                                        {format(
                                          new Date(reply.created_at),
                                          "MMM d, yyyy 'at' h:mm a",
                                        )}
                                        {reply.is_published && (
                                          <Badge
                                            ml={2}
                                            colorPalette="blue"
                                            size="sm"
                                          >
                                            Published
                                          </Badge>
                                        )}
                                      </Text>
                                      <HtmlContent
                                        content={reply.feedback_text}
                                      />
                                    </Box>
                                  ))}
                                </Stack>
                              </Box>
                            )}
                          </Stack>
                        </Card.Body>
                      </Card.Root>
                    ))}
                  </Stack>
                </Box>
              )
            })}
          </Stack>
        )}
      </Stack>
    </PageShell>
  )
}
